import { inject, Injector, runInInjectionContext } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { AnnouncementService } from "../../services/announcement.service";
import { catchError, filter, from, map, mergeMap, of, switchMap } from "rxjs";
import * as AnnouncementActions from './announcement.actions';
import { MatDialog } from "@angular/material/dialog";
import { AnnouncementPopupDialogComponent } from "../../../shared/components/molecules/announcement-popup-dialog/announcement-popup-dialog.component";
import { Announcement } from "../../../shared/models/announcement.model";
import { NotificationService } from "../../services/notification.service";
import { TranslateService } from "@ngx-translate/core";

export const loadPendingAnnouncement$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(AnnouncementService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(AnnouncementActions.loadPendingAnnouncement),
      mergeMap(() =>
        service.getPendingAnnouncement().pipe(
          filter((announcement): announcement is Announcement => !!announcement),
          map(announcement =>
            AnnouncementActions.loadPendingAnnouncementSuccess({ announcement })
          ),
          catchError(error => {
            notification.error(translate.instant('announcement.notifications.loadFailure'));
            return of(AnnouncementActions.loadPendingAnnouncementFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const openAnnouncementPopup$ = createEffect(
  (
    actions$ = inject(Actions),
    dialog = inject(MatDialog),
    injector = inject(Injector)
  ) =>
    actions$.pipe(
      ofType(AnnouncementActions.loadPendingAnnouncementSuccess),
      switchMap(({ announcement }) =>
        from(
          runInInjectionContext(injector, () =>
            dialog.open(AnnouncementPopupDialogComponent, {
              data: { announcement },
              width: '500px',
              disableClose: true
            }).afterClosed()
          )
        ).pipe(
          filter((confirmed: boolean) => confirmed === true),
          map(() =>
            AnnouncementActions.acknowledgeAnnouncement({ announcement_id: announcement.id })
          )
        )
      )
    ),
  { functional: true }
);


export const acknowledgeAnnouncement$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(AnnouncementService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(AnnouncementActions.acknowledgeAnnouncement),
      mergeMap(({ announcement_id }) =>
        service.acknowledgeAnnouncement(announcement_id).pipe(
          map(() => {
            notification.success(translate.instant('announcement.notifications.acknowledgeSuccess'));
            return AnnouncementActions.acknowledgeAnnouncementSuccess();
          }),
          catchError(error => {
            notification.error(translate.instant('announcement.notifications.acknowledgeFailure'));
            return of(AnnouncementActions.acknowledgeAnnouncementFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const loadAllAnnouncements$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(AnnouncementService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(AnnouncementActions.loadAnnouncements),
      mergeMap(() =>
        service.getAllAnnouncements().pipe(
          map(announcements => {
            // Optional:
            // notification.success('Announcements loaded.');
            return AnnouncementActions.loadAnnouncementsSuccess({ announcements });
          }),
          catchError(error => {
            notification.error(translate.instant('announcement.notifications.loadAllFailure'));
            return of(AnnouncementActions.loadAnnouncementsFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const createAnnouncement$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(AnnouncementService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(AnnouncementActions.createAnnouncement),
      mergeMap(action =>
        service.createAnnouncement(action.announcement).pipe(
          map(announcement => {
            notification.success(translate.instant('announcement.notifications.createSuccess'));
            return AnnouncementActions.createAnnouncementSuccess({ announcement });
          }),
          catchError(error => {
            notification.error(translate.instant('announcement.notifications.createFailure'));
            return of(AnnouncementActions.createAnnouncementFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const deleteAnnouncement$ = createEffect(
  (
    actions$ = inject(Actions),
    service = inject(AnnouncementService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(AnnouncementActions.deleteAnnouncement),
      mergeMap(({ id }) =>
        service.deleteAnnouncement(id).pipe(
          map(() => {
            notification.success(translate.instant('announcement.notifications.deleteSuccess'));
            return AnnouncementActions.deleteAnnouncementSuccess({ id });
          }),
          catchError(error => {
            notification.error(translate.instant('announcement.notifications.deleteFailure'));
            return of(AnnouncementActions.deleteAnnouncementFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const AnnouncementEffects = {
    loadPendingAnnouncement$,
    openAnnouncementPopup$,
    acknowledgeAnnouncement$,
    loadAllAnnouncements$,
    createAnnouncement$,
    deleteAnnouncement$
};